import { useEffect, useState } from 'react';
import { SegmentIdentifyUser } from '@components';
import { useRecoilState, useRecoilValue } from 'recoil';

import { ADOBE_API_TOKEN_KEY, onboardingTestIDs } from '@constants';
import { helpers } from '@helpers';
import { useDateTime, useGetAdobeTVEUserAuth } from '@hooks';
import { navigationService } from '@services';
import {
  alertsHeadlinesState,
  currentLocationWeatherState,
  dailyForecastState,
  hourlyForecastState,
  onboardingLandingState,
  pollenIndexState,
  userAuthState,
  userSettingsState,
} from '@store';

import { checkCableAuthorization } from './helpers';

import manifest from '../../../manifest.json';
import { getToken } from '../../hooks/api/useGetAdobeTVEUserAuth';
import { getUserProducts, hasValidProducts } from '../../hooks/api/useGetUserProducts/useGetUserProducts';

export const useLoading = () => {
  useDateTime({ use12HourExtension: false });
  useGetAdobeTVEUserAuth();

  const { isFetched: isAlertsHeadlinesFetched } = useRecoilValue(alertsHeadlinesState);
  const { isFetched: isCurrentLocationWeatherFetched } = useRecoilValue(currentLocationWeatherState);
  // const { isFetched: isAirQualityFetched } = useRecoilValue(airQualityState);
  const { isFetched: isDailyForecastFetched } = useRecoilValue(dailyForecastState);
  const { isFetched: isHourlyForecastFetched } = useRecoilValue(hourlyForecastState);
  const { isFetched: isOnboardingLandingFetched } = useRecoilValue(onboardingLandingState);
  const { isFetched: isPollenIndexFetched } = useRecoilValue(pollenIndexState);
  const { isFetched: isUserAuthFetched } = useRecoilValue(userAuthState);
  const [userSettings, setUserSettings] = useRecoilState(userSettingsState);
  const [userAuth, setUserAuth] = useState(false);
  const [cableAuth, setCableAuth] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  const queries = [
    isAlertsHeadlinesFetched,
    isCurrentLocationWeatherFetched,
    // isAirQualityFetched,
    isDailyForecastFetched,
    isHourlyForecastFetched,
    isOnboardingLandingFetched,
    isPollenIndexFetched,
    isUserAuthFetched,
  ];

  const numberOfFetched = queries.filter(Boolean).length;
  const loadingPercent = Math.round((numberOfFetched / queries.length) * 100);

  useEffect(() => {
    if (helpers.isX1) {
      window.$badger?.dismissLoadingScreen();
    }
  }, []);

  const deviceID = helpers.getDeviceKey();
  let adobeToken = helpers.getStorageItem(ADOBE_API_TOKEN_KEY);

  useEffect(() => {
    if (!userSettings.fetchedFromStorage) {
      return;
    }

    const checkCableUserAuth = async deviceInfoString => {
      const result = await checkCableAuthorization(adobeToken, deviceID, deviceInfoString);

      const hasCableAuth = Boolean(result && !result.error);

      if (hasCableAuth) {
        setCableAuth(hasCableAuth);
      } else {
        // Retry with refreshed token
        // NEED TO FIX
        // eslint-disable-next-line react-hooks/exhaustive-deps
        adobeToken = await getToken(true);
        if (!adobeToken) {
          setCableAuth(false);
        }
        const refreshResult = await checkCableAuthorization(adobeToken, deviceID, deviceInfoString);

        const refreshHadAuth = Boolean(refreshResult && !refreshResult.error);

        setCableAuth(refreshHadAuth);
      }
      setHasCheckedAuth(true);
    };

    const checkPaidUserAuth = async () => {
      const auth = await checkPaidAuthorization();

      setUserAuth(hasValidProducts(auth.data?.products));
      setHasCheckedAuth(true);
    };

    if (userSettings.isCableSubscriber && adobeToken) {
      const { deviceInfoString } = userSettings;

      checkCableUserAuth(deviceInfoString);
    } else if (userSettings.isPaidSubscriber) {
      checkPaidUserAuth();
    } else {
      setUserAuth(false);
      setCableAuth(false);
      setHasCheckedAuth(true);
    }
  }, [userSettings]);

  // If the user is logged then refresh user settings data refetching user products
  const refetchProducts = async (retry = false, isTVEUser) => {
    try {
      const headers = await helpers.getDspAuthHeader(retry);
      const { products, platform } = await getUserProducts(headers);

      if (products.svod.length > 0) {
        const {
          customer,
          product,
          next_assessment_at,
          trial_started_at,
          state,
          previous_state,
          activated_at,
          scheduled_cancellation_at,
        } = products.svod[0];
        const newSettings = {
          ...userSettings,
          firstName: customer.first_name,
          lastName: customer.last_name,
          email: customer.email,
          pricePlan: product.name,
          billDate: next_assessment_at,
          trialStart: trial_started_at,
          subscriptionStatus: state,
          priorSubscriptionStatus: previous_state,
          dspCustomerId: customer.id,
          isPaidSubscriber: true,
          substart: activated_at,
          scheduledcancellationat: scheduled_cancellation_at,
          subscriptionPlatform: platform,
        };

        setUserSettings(newSettings);

        // Identify user with the refreshed data
        await SegmentIdentifyUser(newSettings, isTVEUser);
      }
    } catch (productsError) {
      if (productsError.response) {
        const { status } = productsError.response;
        // retry if token expired

        if (!retry && status === 403) {
          refetchProducts(true);
        }
      }
    }
  };

  useEffect(() => {
    const skipOnboarding = manifest?.debug ? manifest?.skipOnboarding : false;

    if (loadingPercent === 100 && hasCheckedAuth) {
      const timeout = setTimeout(() => {
        // Skip or cable users have no extra logic, go directly to main menu
        if (skipOnboarding || cableAuth) {
          // refetch products only for cable users ignoring skipOnboarding as true
          if (!skipOnboarding) {
            refetchProducts(false, cableAuth);
          }
          return navigationService.replace('MainMenu');
        }
        if (userAuth) {
          // Non-native devices will need device activation.
          // The dspCustomerToken is only set after the device activation flow.
          // Android devices, already registered devices, and cable users do not need to activate.
          if (helpers.isAndroid || userSettings.dspCustomerToken) {
            refetchProducts(false, false);
            return navigationService.replace('MainMenu');
          }
          return navigationService.replace(onboardingTestIDs.pages.deviceActivationPage);
        }

        // We are not signed in with cable or user auth, go to onboarding
        return navigationService.replace(onboardingTestIDs.pages.onboardingHomePage);
      }, 250);

      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingPercent, userAuth, cableAuth, hasCheckedAuth]);

  return loadingPercent;
};
